import * as React from "react"
import { jsonFetch } from "../bookingApi"
import { clearCheckoutSessionStorage } from "../bookingSession"
import { POSTCODE_DEBOUNCE_MS, validateCalendarAvailability } from "../dateSelection"
import type { CalendarAvailability } from "../types"
import type { useBrowserSession } from "./useBrowserSession"
import type { useCheckoutLifecycle } from "./useCheckoutLifecycle"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutNavigation } from "./useCheckoutNavigation"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "postcode" | "settledPostcode" | "setSettledPostcode" | "activeSlug" | "calendarController" | "setCalendarPhase" | "setCalendarError" | "quantity" | "setCalendarSnapshot" | "calendarSnapshot" | "calendarPhase" | "phase" | "confirmationToken" | "initialDraftRef" | "setRange" | "setActiveDateField" | "setAvailability" | "setQuote" | "setPhase" | "setError">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl" | "requestedPostcode" | "calendarContextRef" | "calendarContext" | "calendarRangeError">
    & Pick<ReturnType<typeof useBrowserSession>,
        "browserSessionId">
    & Pick<ReturnType<typeof useCheckoutLifecycle>,
        "cancelAvailabilityCheck" | "resetCheckout">
    & Pick<ReturnType<typeof useCheckoutNavigation>,
        "navigateWorkflow">

export function useCalendarAvailability(inputs: Inputs) {
    const {
        postcode,
        settledPostcode,
        setSettledPostcode,
        activeSlug,
        calendarController,
        setCalendarPhase,
        setCalendarError,
        baseUrl,
        quantity,
        browserSessionId,
        requestedPostcode,
        calendarContextRef,
        calendarContext,
        setCalendarSnapshot,
        calendarSnapshot,
        calendarPhase,
        calendarRangeError,
        phase,
        cancelAvailabilityCheck,
        confirmationToken,
        initialDraftRef,
        resetCheckout,
        setRange,
        setActiveDateField,
        setAvailability,
        setQuote,
        navigateWorkflow,
        setPhase,
        setError,
    } = inputs
    // A single typing debounce feeds both reads. Date/quantity changes do not
    // wait for another timer, and the selected-range check never waits for the calendar.
    React.useEffect(() => {
        if (postcode === settledPostcode) return
        const timer = window.setTimeout(() => setSettledPostcode(postcode), POSTCODE_DEBOUNCE_MS)
        return () => window.clearTimeout(timer)
    }, [postcode, settledPostcode])

    const refreshCalendar = React.useCallback(async () => {
        if (!activeSlug) return
        calendarController.current?.abort()
        const controller = new AbortController()
        calendarController.current = controller
        // Keep the last checked delivery price visible while dates refresh.
        // Readiness still gates progression, including postcode and notice changes.
        setCalendarPhase("loading")
        setCalendarError("")
        try {
            const result = validateCalendarAvailability(await jsonFetch<CalendarAvailability>(`${baseUrl}/v1/calendar-availability`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ productSlug: activeSlug, quantity, browserSessionId, postcode: requestedPostcode || undefined }),
                signal: controller.signal,
            }))
            if (controller.signal.aborted || calendarContextRef.current !== calendarContext) return
            setCalendarSnapshot({ ...result, productSlug: activeSlug, quantity, postcode: requestedPostcode, loadedAt: Date.now() })
            setCalendarPhase("ready")
        } catch {
            if (controller.signal.aborted || calendarContextRef.current !== calendarContext) return
            setCalendarPhase("error")
            setCalendarError("Delivery and collection dates could not be loaded. Try again.")
        } finally {
            if (calendarController.current === controller) calendarController.current = null
        }
    }, [activeSlug, baseUrl, browserSessionId, calendarContext, requestedPostcode, quantity])

    React.useEffect(() => {
        void refreshCalendar()
        return () => { calendarController.current?.abort() }
    }, [refreshCalendar])

    React.useEffect(() => {
        if (!calendarSnapshot || calendarPhase !== "ready") return
        const refreshExpired = () => {
            if (document.visibilityState !== "hidden" && Date.parse(calendarSnapshot.noticeValidUntil) <= Date.now()
                && !calendarController.current) void refreshCalendar()
        }
        const timer = window.setTimeout(refreshExpired, Math.max(1, Date.parse(calendarSnapshot.noticeValidUntil) - Date.now() + 20))
        window.addEventListener("focus", refreshExpired)
        window.addEventListener("pageshow", refreshExpired)
        document.addEventListener("visibilitychange", refreshExpired)
        return () => {
            window.clearTimeout(timer)
            window.removeEventListener("focus", refreshExpired)
            window.removeEventListener("pageshow", refreshExpired)
            document.removeEventListener("visibilitychange", refreshExpired)
        }
    }, [calendarPhase, calendarSnapshot, refreshCalendar])

    React.useEffect(() => {
        if (!calendarRangeError || ["paying", "confirming", "complete"].includes(phase)) return
        cancelAvailabilityCheck()
        const staleToken = confirmationToken || initialDraftRef.current?.checkout?.confirmationToken
        initialDraftRef.current = null
        if (staleToken) {
            // Release the pending checkout using the existing cancellation flow.
            void jsonFetch(`${baseUrl}/v1/cancel-checkout`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ confirmationToken: staleToken }),
            }).catch(() => { /* Server-side checkout expiry remains the fallback. */ })
        }
        resetCheckout()
        clearCheckoutSessionStorage()
        setRange(undefined)
        setActiveDateField("start")
        setAvailability(null)
        setQuote(null)
        navigateWorkflow("dates", "replace")
        setPhase("ready")
        setError(calendarRangeError)
    }, [baseUrl, calendarRangeError, cancelAvailabilityCheck, confirmationToken, navigateWorkflow, phase, resetCheckout])

    return {
        refreshCalendar,
    }
}
